import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  GraduationCap, PencilLine, Save, Download, ArrowLeft, Plus, Trash2,
  ChevronUp, ChevronDown, ImagePlus, X as XIcon, CheckCircle2, RotateCcw,
  AlertTriangle, Users,
} from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { Badge } from "../components/Badges";
import api, { apiErr } from "../lib/api";
import { genKey } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { SOP_CATEGORIES } from "./SopLibrary";

function SopImage({ sopId, step }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let obj;
    if (step.image_url) {
      api.get(step.image_url.replace(/^\/api/, ""), { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
        .catch(() => {});
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [sopId, step.image_url]);
  if (!step.image_url) return null;
  return (
    <div style={{ margin: "10px 0" }}>
      {src ? <img src={src} alt={step.title || "step screenshot"} style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid var(--line)" }} data-testid="sop-step-image" />
           : <div className="s t-mut">Loading image…</div>}
      {step.image_caption && <div className="s t-mut" style={{ marginTop: 4, fontStyle: "italic" }}>{step.image_caption}</div>}
    </div>
  );
}

export default function SopDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { can } = useAuth();
  const canEdit = can("sops", "edit");
  const [sop, setSop] = useState(null);
  const [editing, setEditing] = useState(params.get("edit") === "1");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/sops/${id}`);
      setSop(r.data);
      setForm({ ...r.data, prerequisites: (r.data.prerequisites || []).map((p) => ({ _k: genKey(), value: p })), steps: (r.data.steps || []).map((s) => ({ ...s, _k: genKey() })) });
    } catch (e) { toast.error(apiErr(e)); navigate("/app/sops"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const save = async (statusOverride) => {
    setBusy(true);
    try {
      const body = {
        title: form.title, category: form.category, version: form.version,
        purpose: form.purpose, scope: form.scope,
        prerequisites: form.prerequisites.map((p) => p.value).filter((v) => v.trim()),
        steps: form.steps.map(({ _k, ...rest }) => rest),
        required_reading: !!form.required_reading,
      };
      if (statusOverride) body.status = statusOverride;
      const r = await api.put(`/sops/${id}`, body);
      setSop(r.data);
      setForm({ ...r.data, prerequisites: (r.data.prerequisites || []).map((p) => ({ _k: genKey(), value: p })), steps: (r.data.steps || []).map((s) => ({ ...s, _k: genKey() })) });
      toast.success(statusOverride === "published" ? "SOP published" : statusOverride === "draft" ? "Moved back to draft" : "Saved");
      if (!statusOverride) setEditing(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const r = await api.get(`/sops/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  // ---- Editor helpers ----
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrereq = (i, v) => setForm((f) => { const p = [...f.prerequisites]; p[i] = { ...p[i], value: v }; return { ...f, prerequisites: p }; });
  const addPrereq = () => setForm((f) => ({ ...f, prerequisites: [...f.prerequisites, { _k: genKey(), value: "" }] }));
  const removePrereq = (i) => setForm((f) => ({ ...f, prerequisites: f.prerequisites.filter((_, idx) => idx !== i) }));

  const setStep = (i, patch) => setForm((f) => { const s = [...f.steps]; s[i] = { ...s[i], ...patch }; return { ...f, steps: s }; });
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { _k: genKey(), title: "", instruction: "", note: "", warning: "", image_path: null, image_url: null }] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const moveStep = (i, dir) => setForm((f) => {
    const s = [...f.steps]; const j = i + dir;
    if (j < 0 || j >= s.length) return f;
    [s[i], s[j]] = [s[j], s[i]];
    return { ...f, steps: s };
  });

  const uploadStepImage = async (i, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/sops/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStep(i, { image_path: r.data.image_path, image_url: r.data.image_url });
      toast.success("Screenshot attached");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const acknowledge = async () => {
    try {
      const r = await api.post(`/sops/${id}/acknowledge`);
      setSop((s) => ({ ...s, my_acknowledged_at: r.data.acknowledged_at }));
      toast.success("Thanks — marked as read");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!sop || !form) return <><PageHead crumb="Records" title="SOP" /><Loader /></>;

  return (
    <>
      <PageHead crumb="Records"
        title={editing ? "Edit SOP" : sop.title}
        sub={editing ? "Structured builder — matches the printed SOP format exactly." : `Version ${sop.version} · ${sop.category}`}
        actions={<>
          <button className="btn ghost" onClick={() => navigate("/app/sops")} data-testid="sop-back"><ArrowLeft size={14} /> Back</button>
          <button className="btn ghost" onClick={download} data-testid="sop-detail-dl"><Download size={14} /> Download PDF</button>
          {canEdit && !editing && (
            <button className="btn ghost" onClick={() => setEditing(true)} data-testid="sop-edit-toggle"><PencilLine size={14} /> Edit</button>
          )}
          {canEdit && !editing && sop.status === "draft" && (
            <button className="btn gold" onClick={() => save("published")} disabled={busy} data-testid="sop-publish"><CheckCircle2 size={14} /> Publish</button>
          )}
          {canEdit && !editing && sop.status === "published" && (
            <button className="btn ghost" onClick={() => save("draft")} disabled={busy} data-testid="sop-unpublish"><RotateCcw size={14} /> Move to draft</button>
          )}
        </>} />

      {!editing ? (
        <SopView sop={sop} canEdit={canEdit} onAcknowledge={acknowledge} />
      ) : (
        <div className="card pad" data-testid="sop-editor">
          <div className="form-grid" style={{ marginBottom: 14 }}>
            <div className="fld" style={{ gridColumn: "1 / -1" }}>
              <label>Title</label>
              <input className="input" value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="sop-edit-title" />
            </div>
            <div className="fld">
              <label>Category</label>
              <select className="input" value={form.category} onChange={(e) => setF("category", e.target.value)} data-testid="sop-edit-category">
                {SOP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="fld">
              <label>Version</label>
              <input className="input" value={form.version} onChange={(e) => setF("version", e.target.value)} data-testid="sop-edit-version" />
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 16, cursor: "pointer" }}>
            <input type="checkbox" checked={!!form.required_reading} onChange={(e) => setF("required_reading", e.target.checked)} data-testid="sop-edit-required-reading" />
            Mark as <strong>Required reading</strong> — staff will be asked to confirm they've read it
          </label>

          <div className="fld" style={{ marginBottom: 12 }}>
            <label>Purpose</label>
            <textarea className="input" rows={2} value={form.purpose} onChange={(e) => setF("purpose", e.target.value)} data-testid="sop-edit-purpose" />
          </div>
          <div className="fld" style={{ marginBottom: 12 }}>
            <label>Scope</label>
            <textarea className="input" rows={2} value={form.scope} onChange={(e) => setF("scope", e.target.value)} data-testid="sop-edit-scope" />
          </div>

          <div className="fld" style={{ marginBottom: 16 }}>
            <label>Prerequisites</label>
            {form.prerequisites.map((p, i) => (
              <div key={p._k} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                <input className="input" value={p.value} onChange={(e) => setPrereq(i, e.target.value)} data-testid={`sop-prereq-${i}`} />
                <button className="icon-btn" onClick={() => removePrereq(i)} data-testid={`sop-prereq-remove-${i}`}><XIcon size={14} /></button>
              </div>
            ))}
            <button className="btn sm ghost" onClick={addPrereq} data-testid="sop-prereq-add"><Plus size={12} /> Add prerequisite</button>
          </div>

          <div className="t-strong" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--gold-deep)", margin: "18px 0 10px" }}>
            Procedure — Steps
          </div>
          {form.steps.map((step, i) => (
            <div key={step._k} className="card pad" style={{ background: "var(--card-2)", marginBottom: 12 }} data-testid={`sop-step-editor-${i}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span className="t-strong">Step {i + 1}</span>
                <div style={{ display: "flex", gap: 4 }}>
                  <button className="icon-btn" onClick={() => moveStep(i, -1)} disabled={i === 0} data-testid={`sop-step-up-${i}`}><ChevronUp size={14} /></button>
                  <button className="icon-btn" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1} data-testid={`sop-step-down-${i}`}><ChevronDown size={14} /></button>
                  <button className="icon-btn" style={{ color: "var(--red)" }} onClick={() => removeStep(i)} data-testid={`sop-step-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="fld" style={{ marginBottom: 8 }}>
                <label>Step title</label>
                <input className="input" value={step.title} onChange={(e) => setStep(i, { title: e.target.value })} data-testid={`sop-step-title-${i}`} />
              </div>
              <div className="fld" style={{ marginBottom: 8 }}>
                <label>Instruction</label>
                <textarea className="input" rows={2} value={step.instruction} onChange={(e) => setStep(i, { instruction: e.target.value })} data-testid={`sop-step-instruction-${i}`} />
              </div>
              <div className="fld" style={{ marginBottom: 8 }}>
                <label>Note (optional)</label>
                <textarea className="input" rows={1} value={step.note || ""} onChange={(e) => setStep(i, { note: e.target.value })} data-testid={`sop-step-note-${i}`} />
              </div>
              <div className="fld" style={{ marginBottom: 8 }}>
                <label>Warning (optional)</label>
                <textarea className="input" rows={1} value={step.warning || ""} onChange={(e) => setStep(i, { warning: e.target.value })} data-testid={`sop-step-warning-${i}`} />
              </div>
              <div className="fld">
                <label>Screenshot (optional)</label>
                {step.image_url ? (
                  <div>
                    <SopImage sopId={id} step={step} />
                    <button className="btn sm ghost" onClick={() => setStep(i, { image_path: null, image_url: null })} data-testid={`sop-step-image-remove-${i}`}>
                      <XIcon size={12} /> Remove image
                    </button>
                  </div>
                ) : (
                  <label className="btn sm ghost" style={{ display: "inline-flex", cursor: "pointer" }}>
                    <ImagePlus size={13} /> Upload screenshot
                    <input type="file" accept="image/*" style={{ display: "none" }}
                      onChange={(e) => { if (e.target.files[0]) uploadStepImage(i, e.target.files[0]); e.target.value = ""; }}
                      data-testid={`sop-step-image-input-${i}`} />
                  </label>
                )}
              </div>
            </div>
          ))}
          <button className="btn ghost" onClick={addStep} data-testid="sop-step-add"><Plus size={14} /> Add step</button>

          <div style={{ display: "flex", gap: 10, marginTop: 20, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
            <button className="btn ghost" onClick={() => { setEditing(false); load(); }} disabled={busy} data-testid="sop-edit-cancel">Cancel</button>
            <button className="btn gold" onClick={() => save()} disabled={busy} data-testid="sop-edit-save">
              <Save size={14} /> {busy ? "Saving…" : "Save SOP"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function SopView({ sop, canEdit, onAcknowledge }) {
  return (
    <div className="card pad" data-testid="sop-view">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
        <GraduationCap size={20} />
        <span className="t-strong" style={{ fontSize: 19 }}>{sop.title}</span>
        {sop.status === "published" ? <Badge label="Published" cls="b-green" /> : <Badge label="Draft" cls="b-amber" />}
        {sop.required_reading && <Badge label="Required reading" cls="b-red" testid="sop-required-badge" />}
      </div>
      <div className="s t-mut" style={{ marginBottom: 18 }}>
        {sop.category} · Version {sop.version}
        {sop.status === "published" && sop.published_by_name && sop.published_at
          ? ` · Published by ${sop.published_by_name} on ${String(sop.published_at).slice(0, 10)}`
          : sop.updated_by_name ? ` · Last updated by ${sop.updated_by_name}` : ""}
      </div>

      {sop.required_reading && sop.status === "published" && (
        <div data-testid="sop-ack-banner" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          background: sop.my_acknowledged_at ? "#eef8ef" : "#fdecec",
          border: `1px solid ${sop.my_acknowledged_at ? "#7cc28f" : "#c0303a"}`,
          borderRadius: 8, padding: "10px 14px", marginBottom: 18,
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
            {sop.my_acknowledged_at
              ? <><CheckCircle2 size={16} color="#2f8a45" /> You confirmed you've read this on {String(sop.my_acknowledged_at).slice(0, 10)}.</>
              : <><AlertTriangle size={16} color="#c0303a" /> This is required reading — please confirm you've read and understood it.</>}
          </span>
          {!sop.my_acknowledged_at && (
            <button className="btn sm gold" onClick={onAcknowledge} data-testid="sop-acknowledge-btn">
              <CheckCircle2 size={13} /> I've read this
            </button>
          )}
        </div>
      )}

      {sop.purpose && (<><SectionTitle>Purpose</SectionTitle><p style={{ fontSize: 14, lineHeight: 1.6 }}>{sop.purpose}</p></>)}
      {sop.scope && (<><SectionTitle>Scope</SectionTitle><p style={{ fontSize: 14, lineHeight: 1.6 }}>{sop.scope}</p></>)}
      {sop.prerequisites?.length > 0 && (
        <>
          <SectionTitle>Prerequisites</SectionTitle>
          <ul style={{ fontSize: 14, lineHeight: 1.7, paddingLeft: 20 }}>
            {sop.prerequisites.map((p, i) => <li key={`${i}-${p}`}>{p}</li>)}
          </ul>
        </>
      )}

      {sop.steps?.length > 0 && (
        <>
          <SectionTitle>Procedure</SectionTitle>
          {sop.steps.map((step, i) => (
            <div key={`${i}-${step.title || "step"}`} data-testid={`sop-step-view-${i}`} style={{ marginBottom: 22, paddingBottom: 16, borderBottom: i < sop.steps.length - 1 ? "1px solid var(--line)" : "none" }}>
              <div className="t-strong" style={{ fontSize: 15, marginBottom: 6 }}>Step {i + 1}: {step.title}</div>
              {step.instruction && <p style={{ fontSize: 14, lineHeight: 1.6, margin: "0 0 8px" }}>{step.instruction}</p>}
              <SopImage sopId={sop.id} step={step} />
              {step.note && (
                <div className="callout" style={{ background: "#eef4fb", border: "1px solid #8fb4d8", borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13 }}>
                  <strong style={{ color: "#2c5a8a" }}>NOTE: </strong>{step.note}
                </div>
              )}
              {step.warning && (
                <div className="callout" style={{ background: "#fdecec", border: "1px solid #c0303a", borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13 }}>
                  <strong style={{ color: "#c0303a" }}>WARNING: </strong>{step.warning}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {canEdit && sop.required_reading && <AcknowledgementsPanel sopId={sop.id} />}
    </div>
  );
}

function AcknowledgementsPanel({ sopId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/sops/${sopId}/acknowledgements`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [sopId]);
  if (!data) return null;
  return (
    <div data-testid="sop-acknowledgements-panel" style={{ marginTop: 24, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Users size={15} />
        <span className="t-strong" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--gold-deep)" }}>
          Acknowledgements — {data.acknowledgements.length}/{data.total_staff} staff confirmed
        </span>
      </div>
      {data.acknowledgements.length === 0 ? (
        <p className="s t-mut">No one has confirmed reading this yet.</p>
      ) : (
        <ul style={{ fontSize: 13.5, lineHeight: 1.8, paddingLeft: 18 }}>
          {data.acknowledgements.map((a, i) => (
            <li key={a.user_id || i}>{a.user_name} <span className="t-mut">({a.user_role}) — {String(a.acknowledged_at).slice(0, 10)}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="t-strong" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--gold-deep)", margin: "18px 0 8px" }}>{children}</div>;
}
